import { IListenerConfig, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { EasmAssetsInventoryGridView } from '../view/EasmAssetsInventoryGridView';
import { EasmAssetsInventoryGridWebComponent } from '../component/EasmAssetsInventoryGridWebComponent';
import { EasmAssetsInventoryRepository } from '../repository/EasmAssetsInventoryRepository';
import easmAssetsInventoryGridTemplate from '../view/template';
import {
  AssetAction,
  ColumnKeys,
  EasmAssetsInventoryGridViewData,
  IncludeExcludeAsset,
  UPDATE_EXCLUDED_ASSETS_METHOD,
  UPDATE_INCLUDED_ASSETS_METHOD,
} from '../types';
import { SmartViewEvents } from '../../../grid/genericGridSmartViews/types';
import { GridEvents } from '../../../grid/types/common';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { EASMSidePanelEvents } from '../../findingDetails/findingDetails/types';
import { FilterChangeEvents } from '../../../grid/gridFilters/types';
import { EasmInventoryBaseGridController } from './EasmInventoryBaseGridController';
import { Events, FindingClassType, FindingType } from '../../types';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { getErrorMessage } from '../../utils';
import { translateFn } from '../../../utils/translator.helper';
import { ResponseFailReason } from '../../scanConfiguration/easmScanConfiguration/types';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { Events as BulkChangePropertyEvents, Property } from '../../bulkChangeProperty/types';
import { GzSelectMenuWithButtonEvents } from '../../../ui/gzSelectMenuWithButton/types';

export class EasmAssetsInventoryGridController extends EasmInventoryBaseGridController {
  public component: EasmAssetsInventoryGridWebComponent;
  public dataRepository: EasmAssetsInventoryRepository;
  public view: EasmAssetsInventoryGridView;

  public constructor(component: EasmAssetsInventoryGridWebComponent) {
    super(component);

    this.initialSetup({
      dataRepositoryConstructor: EasmAssetsInventoryRepository,
      viewConstructor: EasmAssetsInventoryGridView,
      viewTemplate: easmAssetsInventoryGridTemplate,
    });
  }

  public getCompanyIdFilterValue(): string {
    return this.view.getCompanyIdFilterValue();
  }

  public computeEventListeners(): IListenerConfig[] {
    this.view.dom.$gridView.onDataLoadingFinishedWithSuccess = this.onDataLoadingFinishedWithSuccess.bind(this);
    return [
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.onSmartViewSelect,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
        callback: this.onSmartViewCreated,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.updateViewProps,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
        callback: this.showSaveAsDialog,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.handleSelectionChange,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED,
        callback: this.handleSmartViewFavoriteChanged,
      },
      {
        element: this.view.dom.$scanConfigurationBtn,
        event: ButtonEvents.CLICK,
        callback: this.openScanConfigurationWithResults,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClick.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EXPORT_CSV,
        callback: this.handleCSVExportEvent,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: EASMSidePanelEvents.SHOW_NEW_ASSET,
        callback: this.showNewAssetSidePanel,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: SideMenuEvents.CLOSE,
        callback: this.handleDetailsPanelClose,
      },
      {
        element: this.view.dom.$changeScanActionBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.SCAN_ACTION, event);
        },
      },
      {
        element: this.view.dom.$changeInvestigationStatusBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.INVESTIGATION_STATUS, event);
        },
      },
      {
        element: this.view.dom.$assignBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.ASSIGNEE, event);
        },
      },
      {
        element: this.view.dom.$prioritizeBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.PRIORITY, event);
        },
      },
      {
        element: this.view.dom.$gridView,
        event: FilterChangeEvents.REMOTE_SELECT_CHANGED,
        callback: this.handleCompanyFilterUpdated,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.GRID_LOADED,
        callback: this.openDetailsPanelIfDemanded,
      },
      {
        element: this.view.dom.$gridView,
        event: Events.CHANGE_PROPERTY,
        callback: this.handleChangeProperty,
      },
      {
        element: document.body,
        event: BulkChangePropertyEvents.PROPERTY_CHANGED,
        callback: (): Promise<void> => this.refreshGrid(true),
      },
    ];
  }

  public async getDataForView(): Promise<EasmAssetsInventoryGridViewData> {
    return this.dataRepository.getDataForView();
  }

  private async handleDetailsPanelClose(): Promise<void> {
    if (this.view.dom.$detailsPanel.shouldGridBeRefreshed) {
      await this.refreshGrid(false);
    }
  }

  private handleSelectionChange(event: CustomEvent): void {
    this.selectedItemIds = event.detail.selectionStoreData;

    const noItemsSelected = 0 === this.selectedItemIds.length;

    this.view.dom.$changeScanActionBtn.disabled = noItemsSelected;
    this.view.dom.$changeInvestigationStatusBtn.disabled = noItemsSelected;
    this.view.dom.$assignBtn.disabled = noItemsSelected;
    this.view.dom.$prioritizeBtn.disabled = noItemsSelected;
  }

  protected complexEnumFilterColumns(): string[] {
    return [ColumnKeys.NAME, ColumnKeys.ASSETS_RELATED_TO_ASSET, ColumnKeys.ARTIFACTS_RELATED_TO_ASSET];
  }

  protected findingClassType(): FindingClassType {
    return FindingClassType.ASSET;
  }
}
